import './styles/index.css';

import { ThemeProvider } from '@inmarket/inmarkit';
import { LicenseInfo } from '@mui/x-license';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React, { useEffect } from 'react';
import TagManager from 'react-gtm-module';
import { ModalProvider } from 'react-modal-hook';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  useLocation,
} from 'react-router-dom';

import ClientsList from './components/Clients/ClientsList';
import ContractConfiguration from './components/Contracts/ContractConfiguration';
import Layout from './components/layout/Layout';
import {
  ADMIN_CONTRACT_CONFIGURATION_URL,
  ADMIN_CONTRACT_EDIT_URL,
  ADMIN_CONTRACT_NEW_URL,
  ADMIN_CONTRACT_REPORTS_URL,
  ADMIN_CONTRACTS_URL,
  ADVERTISERS_CREATIVES_EDIT_URL,
  ADVERTISERS_CREATIVES_NEW_URL,
  ADVERTISERS_EDIT_URL,
  ALERTS_URL,
  CLIENTS_ADVERTISERS_EDIT_URL,
  CLIENTS_ADVERTISERS_NEW_URL,
  CLIENTS_EDIT_URL,
  CLIENTS_LIST_URL,
  CLIENTS_NEW_URL,
  CLIENTS_USERS_EDIT_URL,
  CLIENTS_USERS_NEW_URL,
  CUSTOM_SEGMENTS_EDIT_URL,
  CUSTOM_SEGMENTS_LIST_URL,
  CUSTOM_SEGMENTS_NEW_URL,
  EDIT_AD_GROUP_URL,
  EDIT_LINE_ITEM_URL,
  LOGIN_URL,
  NEW_AD_GROUP_URL,
  NEW_LINE_ITEM_URL,
  PIXEL_GENERATOR_URL,
  PREVIEW_STUDIO_URL,
  PREVIEW_TOOL_URL,
  PRODUCTS_EDIT_URL,
  PRODUCTS_NEW_URL,
  REPORTING_HUB_EDIT_DEFAULT_URL,
  REPORTING_HUB_EDIT_URL,
  REPORTING_HUB_NEW_URL,
  REPORTING_HUB_URL,
  SHARED_APP_LIST_EDIT_URL,
  SHARED_APP_LIST_NEW_URL,
  SHARED_APP_LIST_URL,
  SSO_LOGIN_URL,
} from './constants/routeUrls';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { ConfigProvider, useConfig } from './hooks/useConfig';
import { ConstantsProvider } from './hooks/useConstants';
import { SnackBarProvider } from './hooks/useSnackBar';
import AdGroupPage from './pages/AdGroupPage';
import AdvertisersCreativesFormPage from './pages/AdvertisersCreativesFormPage';
import AlertsPage from './pages/AlertsPage';
import ClientsAdvertisersFormPage from './pages/ClientsAdvertisersFormPage';
import ClientsFormPage from './pages/ClientsFormPage';
import ClientsUsersFormPage from './pages/ClientsUsersFormPage';
import ContractPage from './pages/ContractPage';
import ContractReportsPage from './pages/ContractReportsPage';
import ContractsPage from './pages/ContractsPage';
import CustomSegmentsFormPage from './pages/CustomSegmentsFormPage';
import CustomSegmentsListPage from './pages/CustomSegmentsListPage';
import LineItemPage from './pages/LineItemPage';
import LoginPage from './pages/LoginPage';
import NewAdGroupPage from './pages/NewAdGroupPage';
import NewContractPage from './pages/NewContractPage';
import NewLineItemPage from './pages/NewLineItemPage';
import PixelGeneratorPage from './pages/PixelGeneratorPage';
import PreviewGalleryPage from './pages/PreviewGalleryPage';
import PreviewStudioPage from './pages/PreviewStudioPage';
import PreviewToolPage from './pages/PreviewToolPage';
import ProductPage from './pages/ProductPage';
import ReportingHubEditDefaultPage from './pages/ReportingHubEditDefaultPage';
import ReportingHubEditPage from './pages/ReportingHubEditPage';
import ReportingHubNewPage from './pages/ReportingHubNewPage';
import ReportingHubPage from './pages/ReportingHubPage';
import SharedAppFormPage from './pages/SharedAppFormPage';
import SharedAppListPage from './pages/SharedAppListPage';
import SSOLogin from './pages/SSOLogin';
import oneDspTheme from './theme/oneDspTheme';
import { getGTMEnvironment } from './util/googleTagManager';
import posthog, { initPostHog } from './util/posthog';

LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_X_LICENSE_KEY);

// initialize google tag manager
const tagManagerArgs = getGTMEnvironment();
TagManager.initialize(tagManagerArgs);

initPostHog();

// instantiate react-query client
const queryClient = new QueryClient();

function PostHogPageView() {
  const location = useLocation();
  useEffect(() => {
    posthog.capture('$pageview', { $current_url: window.location.href });
  }, [location.pathname]);
  return null;
}

function LoginCheck() {
  const { user, ssoLogin } = useAuth();
  const { apiHost } = useConfig();

  if (user) {
    return <Navigate to="/admin/contracts" replace />;
  }

  if (import.meta.env.VITE_IS_FEDERATED === 'true') {
    ssoLogin(apiHost);
  }
  return <LoginPage />;
}

function ProtectedRoute() {
  const auth = useAuth();
  const location = useLocation();

  // If the user is not logged in, redirect to the login page
  // pass also the originally requested path for redirecting back after login
  if (!auth.user) {
    return <Navigate to={LOGIN_URL} state={{ from: location }} replace />;
  }

  return <Outlet />;
}

const createCustomRouter = ({ basename }) =>
  createBrowserRouter(
    createRoutesFromElements(
      <Route
        element={
          <AuthProvider>
            <ConstantsProvider>
              <ModalProvider>
                <PostHogPageView />
                <Layout />
              </ModalProvider>
            </ConstantsProvider>
          </AuthProvider>
        }
      >
        <Route path={SSO_LOGIN_URL} element={<SSOLogin />} />
        <Route path={LOGIN_URL} element={<LoginCheck />} />
        {/* Public routes (no auth) */}
        <Route path="/shared_gallery/:publicRoute" element={<PreviewGalleryPage />} />
        {/* Auth protected route nesting */}
        <Route element={<ProtectedRoute />}>
          <Route path={ALERTS_URL} element={<AlertsPage />} />
          <Route path={ADMIN_CONTRACTS_URL} element={<ContractsPage />} />
          <Route path={ADMIN_CONTRACT_CONFIGURATION_URL} element={<ContractConfiguration />}>
            <Route path={ADMIN_CONTRACT_NEW_URL} element={<NewContractPage />} />
            <Route path={ADMIN_CONTRACT_EDIT_URL} element={<ContractPage />} />
            <Route path={NEW_LINE_ITEM_URL} element={<NewLineItemPage />} />
            <Route path={EDIT_LINE_ITEM_URL} element={<LineItemPage />} />
            <Route path={NEW_AD_GROUP_URL} element={<NewAdGroupPage />} />
            <Route path={EDIT_AD_GROUP_URL} element={<AdGroupPage />} />
          </Route>
          <Route path={CUSTOM_SEGMENTS_LIST_URL} element={<CustomSegmentsListPage />} />
          <Route path={CUSTOM_SEGMENTS_NEW_URL} element={<CustomSegmentsFormPage />} />
          <Route path={CUSTOM_SEGMENTS_EDIT_URL} element={<CustomSegmentsFormPage />} />
          <Route path={CLIENTS_LIST_URL} element={<ClientsList />} />
          <Route path={CLIENTS_NEW_URL} element={<ClientsFormPage />} />
          <Route path={CLIENTS_EDIT_URL} element={<ClientsFormPage />} />
          <Route path={CLIENTS_USERS_NEW_URL} element={<ClientsUsersFormPage />} />
          <Route path={CLIENTS_USERS_EDIT_URL} element={<ClientsUsersFormPage />} />
          <Route path={ADVERTISERS_EDIT_URL} element={<ClientsAdvertisersFormPage />} />
          <Route path={CLIENTS_ADVERTISERS_EDIT_URL} element={<ClientsAdvertisersFormPage />} />
          <Route path={CLIENTS_ADVERTISERS_NEW_URL} element={<ClientsAdvertisersFormPage />} />
          <Route path={ADVERTISERS_CREATIVES_EDIT_URL} element={<AdvertisersCreativesFormPage />} />
          <Route path={ADVERTISERS_CREATIVES_NEW_URL} element={<AdvertisersCreativesFormPage />} />
          <Route path={PREVIEW_TOOL_URL} element={<PreviewToolPage />} />
          <Route path={PREVIEW_STUDIO_URL} element={<PreviewStudioPage />} />
          <Route path={PRODUCTS_NEW_URL} element={<ProductPage />} />
          <Route path={PRODUCTS_EDIT_URL} element={<ProductPage />} />
          <Route path={PIXEL_GENERATOR_URL} element={<PixelGeneratorPage />} />
          <Route path={ADMIN_CONTRACT_REPORTS_URL} element={<ContractReportsPage />} />
          <Route path={REPORTING_HUB_URL} element={<ReportingHubPage />} />
          <Route path={REPORTING_HUB_NEW_URL} element={<ReportingHubNewPage />} />
          <Route path={REPORTING_HUB_EDIT_URL} element={<ReportingHubEditPage />} />
          <Route path={REPORTING_HUB_EDIT_DEFAULT_URL} element={<ReportingHubEditDefaultPage />} />
          <Route path={SHARED_APP_LIST_URL} element={<SharedAppListPage />} />
          <Route path={SHARED_APP_LIST_NEW_URL} element={<SharedAppFormPage />} />
          <Route path={SHARED_APP_LIST_EDIT_URL} element={<SharedAppFormPage />} />
        </Route>
        {/* Redirect to login if no match */}
        <Route path="*" element={<Navigate to={LOGIN_URL} replace />} />
      </Route>
    ),
    {
      basename: basename || '/',
    }
  );

// TODO: move providers
// eslint-disable-next-line react/prop-types
function App() {
  return (
    <ConfigProvider>
      <ThemeProvider theme={oneDspTheme}>
        <SnackBarProvider>
          <QueryClientProvider client={queryClient}>
            <RouterProvider
              router={createCustomRouter({
                basename: import.meta.env.VITE_IS_FEDERATED === 'true' ? '/onedsp' : '/',
              })}
            />
            <ReactQueryDevtools />
          </QueryClientProvider>
        </SnackBarProvider>
      </ThemeProvider>
    </ConfigProvider>
  );
}

export default App;
